import { collection, getDocs, query, doc, getDoc } from 'firebase/firestore';
import { db } from "@/firebase/init";

/**
 * Fetch all semester tests from the modules collection
 * @returns {Promise<Object>} Object containing success status and tests data or error message
 */
export async function fetchAllSemesterTests() {
  try {
    // Query the modules collection to get test data
    const modulesRef = collection(db, 'modules');
    const modulesSnapshot = await getDocs(query(modulesRef));
    
    const tests = [];
    
    modulesSnapshot.forEach(doc => {
      const moduleData = doc.data();
      const moduleCode = moduleData.moduleCode;
      
      // Check if module has semester test data
      if (moduleData.SemesterTestData && Array.isArray(moduleData.SemesterTestData)) {
        // Add each test with its module code
        moduleData.SemesterTestData.forEach(test => {
          tests.push({
            moduleCode,
            ...test
          });
        });
      }
    });
    
    return {
      success: true,
      data: tests
    };
  } catch (error) {
    console.error('Error fetching semester tests:', error);
    return {
      success: false,
      message: error.message || 'Failed to load semester tests'
    };
  }
}

/**
 * Fetch semester tests for specific modules - OPTIMIZED VERSION
 * This function fetches each module document directly instead of querying the entire collection
 * @param {Array<string>} moduleCodes - Array of module codes to fetch tests for
 * @returns {Promise<Object>} Object containing success status and tests data or error message
 */
export async function fetchSemesterTestsByModules(moduleCodes) {
  try {
    if (!Array.isArray(moduleCodes) || moduleCodes.length === 0) {
      return {
        success: true,
        data: []
      };
    }
    
    const tests = [];
    const uniqueModuleCodes = [...new Set(moduleCodes)]; // Remove duplicates
    
    // Process each module code to extract the base code (before any spaces or special characters)
    const baseModuleCodes = uniqueModuleCodes.map(code => {
      // Extract base module code (e.g., "COS 132" -> "COS132")
      const baseCode = code.replace(/\s+/g, ''); 
      return baseCode;
    });

    // For each unique module code, fetch directly from Firestore
    const promises = baseModuleCodes.map(async (baseCode) => {
      // First try with the exact code
      const moduleDocRef = doc(db, 'modules', baseCode);
      let moduleDoc = await getDoc(moduleDocRef);
      
      if (!moduleDoc.exists()) {
        // If not found, try fetching all modules and filter by prefix
        const modulesSnapshot = await getDocs(query(collection(db, 'modules')));
        const matchingDocs = [];
        
        modulesSnapshot.forEach(firestoreDoc => {
          const moduleData = firestoreDoc.data();
          const code = moduleData.moduleCode;
          
          // Check if this module code matches any of our base codes (case insensitive starts with)
          if (code && baseModuleCodes.some(baseCode => 
            code.replace(/\s+/g, '').toLowerCase().startsWith(baseCode.toLowerCase())
          )) {
            matchingDocs.push(moduleData);
          }
        });
        
        // Process matching documents
        matchingDocs.forEach(moduleData => {
          if (moduleData.SemesterTestData && Array.isArray(moduleData.SemesterTestData)) {
            moduleData.SemesterTestData.forEach(test => {
              tests.push({
                moduleCode: moduleData.moduleCode,
                ...test
              });
            });
          }
        });
      } else {
        // Process the directly fetched document
        const moduleData = moduleDoc.data();
        if (moduleData.SemesterTestData && Array.isArray(moduleData.SemesterTestData)) {
          moduleData.SemesterTestData.forEach(test => {
            tests.push({
              moduleCode: moduleData.moduleCode,
              ...test
            });
          });
        }
      }
    });
    
    // Wait for all document fetches to complete
    await Promise.all(promises);
    
    return {
      success: true,
      data: tests
    };
  } catch (error) {
    console.error('Error fetching semester tests by modules:', error);
    return {
      success: false,
      message: error.message || 'Failed to load semester tests for selected modules'
    };
  }
}

/**
 * Format date string (e.g., "28 Mar 2025") into a JavaScript Date object
 * @param {string} dateStr - Date string in format "DD MMM YYYY"
 * @returns {Date} JavaScript Date object
 */
export function parseTestDate(dateStr) {
  // Parse dates in format "28 Mar 2025"
  const [day, month, year] = dateStr.split(' ');
  
  const months = {
    'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
    'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
  };
  
  return new Date(parseInt(year), months[month], parseInt(day));
}

/**
 * Get the color class for a test based on its date
 * @param {Date} testDate - Date of the test
 * @returns {string} CSS class string for styling
 */
export function getColorForTest(testDate) {
  const now = new Date();
  const diffTime = testDate - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays <= 7) return 'bg-red-100 text-red-800 border-red-200';
  if (diffDays <= 14) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-blue-100 text-blue-800 border-blue-200';
}
